import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { apiGet, apiPost, errOf } from '../lib/api';
import { brandAppName } from '../brand';

interface RsvpInfo {
  name: string;
  locale: string;
  event: { name: string; starts_at: string | null; venue: string } | null;
  purpose: string;
  rsvp_status: string;
  rsvp_guests: number;
  responded: boolean;
}

/**
 * 公開 RSVP 頁（`/r/<token>`；**唔需要登入**）—— 邀請函入面嘅專屬連結。
 * 🔐 token 由信件帶入；API 用 hash 查（DB 只存 hash）。
 * ⚠️ 用**獨立** shell（唔入會員／理事會 app），收件人可能唔係會員。
 */
export default function RsvpPage() {
  const { t, i18n } = useTranslation();
  const { token = '' } = useParams();
  const [info, setInfo] = useState<RsvpInfo | null>(null);
  const [guests, setGuests] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState('');

  const load = useCallback(async () => {
    const r = await apiGet<RsvpInfo>(`/api/rsvp/${token}`);
    if (!r.ok || !r.data) {
      setErr(errOf(r.data) || t('rsvp_invalid'));
      return;
    }
    setInfo(r.data);
    setGuests(r.data.rsvp_guests);
    if (r.data.rsvp_status === 'accepted') setDone(t('rsvp_sent_accept'));
    if (r.data.rsvp_status === 'declined') setDone(t('rsvp_sent_decline'));
  }, [token, t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function respond(status: 'accepted' | 'declined') {
    setBusy(true);
    setErr('');
    const r = await apiPost(`/api/rsvp/${token}`, { status, guests });
    setBusy(false);
    if (!r.ok) {
      setErr(errOf(r.data) || t('rsvp_failed'));
      return;
    }
    setDone(status === 'accepted' ? t('rsvp_sent_accept') : t('rsvp_sent_decline'));
    await load();
  }

  return (
    <AppShell title={brandAppName(i18n.language)}>
      <div className="max-w-lg mx-auto p-5">
        <h2 className="font-bold text-lg mb-3">{t('rsvp_title')}</h2>

        {!info ? (
          <p className="text-sm" style={{ color: err ? 'var(--color-danger)' : 'var(--color-text-secondary)' }}>
            {err || '…'}
          </p>
        ) : (
          <>
            <div className="rounded-xl p-4 mb-4" style={{ background: 'var(--color-card)', boxShadow: 'var(--shadow-card)' }}>
              <p className="font-semibold mb-1">{t('rsvp_greeting', { name: info.name || '—' })}</p>
              {info.event && (
                <ul className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  <li>
                    {t('rsvp_event')}：{info.event.name}
                  </li>
                  {info.event.starts_at && (
                    <li>
                      {t('rsvp_date')}：{info.event.starts_at.slice(0, 16).replace('T', ' ')}
                    </li>
                  )}
                  {info.event.venue && (
                    <li>
                      {t('rsvp_venue')}：{info.event.venue}
                    </li>
                  )}
                </ul>
              )}
            </div>

            {done && (
              <p className="text-sm mb-3" style={{ color: 'var(--color-primary)' }}>
                {done}
              </p>
            )}

            <label className="block text-xs mb-1" style={{ color: 'var(--color-text-secondary)' }}>
              {t('rsvp_guests')}
            </label>
            <select
              className="w-full rounded-lg px-3 py-2 mb-4"
              style={{ border: '1px solid var(--color-divider)', background: 'var(--color-bg)', color: 'var(--color-text)' }}
              value={guests}
              onChange={(e) => setGuests(Number(e.target.value))}
            >
              {[0, 1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>

            <div className="flex gap-3">
              <button
                type="button"
                disabled={busy}
                onClick={() => void respond('accepted')}
                className="flex-1 rounded-lg px-4 py-2.5 font-semibold"
                style={{ background: 'var(--color-primary)', color: 'var(--color-on-primary)', border: 'none', cursor: 'pointer' }}
              >
                {t('rsvp_accept')}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void respond('declined')}
                className="flex-1 rounded-lg px-4 py-2.5 font-semibold"
                style={{ background: 'transparent', color: 'var(--color-text-secondary)', border: '1px solid var(--color-divider)', cursor: 'pointer' }}
              >
                {t('rsvp_decline')}
              </button>
            </div>
            {err && (
              <p className="text-sm mt-3" style={{ color: 'var(--color-danger)' }}>
                {err}
              </p>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}
